// Estado de sesion compartido: quien es el usuario, su rol y si tiene un
// cambio de contrasena pendiente. El token y el perfil se guardan para
// sobrevivir a un refresco de pagina.
import { useCallback, useEffect, useState } from 'react';
import { api, avisarmeSiSePierdeLaSesion, haySesion, marcarSesion } from './api';
import { cumpleNivel } from './lib/roles';
import { ContextoAuth } from './lib/contextoAuth';
import { limpiarTrabajo } from './lib/cacheTrabajo';
import { guardarBorrador, olvidarBorrador } from './lib/borradores';
import { recordarClaveEmpresa } from './lib/claveEmpresa';
import { aplicarAspectoDeLaCuenta } from './lib/tema';
import AsistenteSegundoFactor from './seguridad/AsistenteSegundoFactor';
import NoFuiYo from './seguridad/NoFuiYo';
import PuertaIdentidad from './seguridad/PuertaIdentidad';
import RecuperarMiCuenta from './seguridad/RecuperarMiCuenta';
import RecuperacionNoFuiYo from './seguridad/RecuperacionNoFuiYo';
import { enlaceDeRecuperacion, enlaceNoFuiYoRecuperacion } from './seguridad/recuperacionEnlace';
import { datosDelEnlace, debeVerAsistente } from './seguridad/segundoFactor';
import { navegacion } from './ui/navegacion';
import NavegacionDePuerta from './ui/NavegacionDePuerta';
import { entrarConLlave, olvidarPasoSeguro } from './seguridad/llaveDeAcceso';
import TarjetaEntraConUnToque from './seguridad/TarjetaEntraConUnToque';

// «HACERLO DESPUÉS» DURA LO QUE DURA ESTA PESTAÑA · ESTABILIDAD D1. Se
// guarda en `sessionStorage`, no en `localStorage`: en el siguiente acceso
// el asistente vuelve a aparecer, que es lo que hace que «después» no se
// convierta en «nunca».
const CLAVE_POSPUESTO = 'tego_2fa_pospuesto';
function leerPospuesto() {
  try { return sessionStorage.getItem(CLAVE_POSPUESTO) === '1'; } catch { return false; }
}

const CLAVE_USUARIO = 'tego_usuario';

function leerUsuarioGuardado() {
  try {
    const crudo = localStorage.getItem(CLAVE_USUARIO);
    return crudo ? JSON.parse(crudo) : null;
  } catch {
    return null;
  }
}

const CLAVE_SESION = 'tego_sesion';

// EL CANDADO ENTRE PESTAÑAS · T-14. La hora del último refresco de la
// credencial, apuntada en `localStorage` —que SÍ comparten las pestañas
// del mismo navegador—. La credencial es una cookie del mismo origen, así
// que todas las pestañas la comparten: si una acaba de renovar, las demás
// ya tienen la cookie fresca y no necesitan pedir otra. Sin esto, volver a
// cinco pestañas a la vez dispara cinco refrescos que compiten por lo
// mismo.
const CLAVE_RENOVADA = 'tego_sesion_renovada';
// Si otra pestaña renovó hace menos de esto, esta se ahorra la petición.
// Corto —la credencial dura una hora— pero basta para que varias pestañas
// que vuelven a la vez no disparen todas su refresco.
const MARGEN_RENOVACION_MS = 20_000;

function leerAjustesGuardados() {
  try {
    const crudo = localStorage.getItem(CLAVE_SESION);
    return crudo ? JSON.parse(crudo) : null;
  } catch {
    return null;
  }
}

export function ProveedorAuth({ children }) {
  const [usuario, setUsuario] = useState(() =>
    haySesion() ? leerUsuarioGuardado() : null,
  );

  // POR QUÉ SE CERRÓ LA SESIÓN · deudas T-a y T-b.
  //
  // `null` mientras hay sesión. Al cerrarse toma uno de tres valores, y
  // de eso depende adónde va la persona y qué se le dice:
  //
  //   · 'CADUCADA'     — el servidor contestó 401. Se acabó el tiempo.
  //   · 'INACTIVIDAD'  — no se tocó nada en los minutos que diga el
  //                      servidor.
  //   · 'A_MANO'       — le dio a «Cerrar sesión». No se le explica nada:
  //                      ya sabe lo que hizo.
  //
  // Con los dos primeros se va DIRECTO a la pantalla de acceso, no a la
  // página pública: quien estaba trabajando quiere volver, no que le
  // vendan el producto otra vez.
  const [motivoCierre, setMotivoCierre] = useState(null);

  // Los tres números de la sesión, DICHOS POR EL SERVIDOR. Si estuvieran
  // escritos aquí, el día que alguien cambiara `INACTIVIDAD_MINUTOS` en
  // el `.env` la pantalla seguiría avisando con el número viejo.
  const [ajustesSesion, setAjustesSesion] = useState(leerAjustesGuardados);

  useEffect(() => {
    if (usuario) localStorage.setItem(CLAVE_USUARIO, JSON.stringify(usuario));
    else localStorage.removeItem(CLAVE_USUARIO);
  }, [usuario]);

  // CON SESIÓN, ATRÁS AVISA ANTES DE SACAR DE TEGO, TAMBIÉN EN LAS
  // PANTALLAS PREVIAS A LA APLICACIÓN · TC-DR-AT, auditoría H2. Con el
  // asistente del segundo factor a la vista, el primer atrás cargaba el
  // portal sin aviso: la aplicación todavía no se había montado y nadie
  // le había dicho al módulo de navegación que había sesión.
  const conSesion = !!usuario;
  useEffect(() => {
    navegacion.conSesion(conSesion);
    return () => navegacion.conSesion(false);
  }, [conSesion]);

  // LOS PERMISOS SE RELEEN DEL SERVIDOR, NO SE GUARDAN Y YA.
  //
  // El menú se filtraba solo por rol, así que conceder un permiso
  // individual abría la puerta por la API y ninguna por la interfaz.
  // Ahora el menú los mira — pero si se quedaran congelados en el
  // localStorage del día que se inició sesión, un permiso RETIRADO
  // seguiría enseñando su módulo hasta que la persona volviera a entrar,
  // y el sistema estaría diciendo algo que ya no es cierto.
  //
  // El backend relee al usuario en cada petición justamente por esto;
  // aquí se hace lo equivalente al abrir la aplicación. Si la llamada
  // falla no se toca nada: la pantalla es comodidad, y el guardián de
  // verdad sigue siendo el servidor.
  // RELEER EL PERFIL DEL SERVIDOR. Se usa al abrir la aplicación y, otra
  // vez, al volver al frente (`tego:volvio-al-frente`, que manda
  // `useInactividad`): quien deja TEGO media hora en otra pestaña vuelve a
  // permisos y datos que pueden haber cambiado, y así se ponen al día sin
  // recargar. Si la llamada falla no se toca nada: la pantalla es
  // comodidad y el guardián de verdad sigue siendo el servidor.
  const recargarPerfil = useCallback(() => {
    if (!haySesion()) return;
    api.get('/auth/yo')
      .then((yo) => {
        // EL ASPECTO GUARDADO EN LA CUENTA (TODO-SIRVE F2-B): lo que se eligió
        // en «Mi cuenta» desde otro aparato se aplica aquí al abrir.
        aplicarAspectoDeLaCuenta(yo.aspecto);
        setUsuario((u) => (u ? {
          ...u,
          permisos: yo.permisos || [],
          // Si el servidor no manda el dato, se deja como estaba en vez de
          // suponer que falta confirmar.
          ...(yo.correo_verificado === undefined
            ? {} : { correo_verificado: yo.correo_verificado !== false }),
          // La foto de la cabecera (CUENTA A, 19 sep 2026): null es «no
          // se miró» y no toca lo que hubiera.
          ...(yo.tiene_foto === undefined || yo.tiene_foto === null
            ? {} : { tiene_foto: !!yo.tiene_foto }),
        } : u));
      })
      .catch(() => {});
  }, []);

  useEffect(() => { recargarPerfil(); }, [recargarPerfil]);

  // AL VOLVER AL FRENTE, LA PANTALLA SE ACTUALIZA SOLA · T-14. La sesión
  // (permisos, foto) se relee aquí; cada pantalla que quiera refrescar SUS
  // datos escucha el mismo aviso. `useInactividad` ya renovó la credencial
  // en el acto, así que esta relectura viaja con la sesión ya fresca.
  useEffect(() => {
    window.addEventListener('tego:volvio-al-frente', recargarPerfil);
    return () => window.removeEventListener('tego:volvio-al-frente', recargarPerfil);
  }, [recargarPerfil]);

  // LA CABECERA CAMBIA SIN RECARGAR. «Mi cuenta» avisa cuando sube o
  // quita la foto (`seguridad/foto.js`), y aquí se apunta en el usuario
  // de la sesión, que es lo que lee la cabecera.
  useEffect(() => {
    const alCambiar = (e) => {
      const d = e?.detail || {};
      setUsuario((u) => (u ? { ...u, tiene_foto: !!d.tiene_foto, sello_foto: d.sello || Date.now() } : u));
    };
    window.addEventListener('tego:foto-de-perfil', alCambiar);
    return () => window.removeEventListener('tego:foto-de-perfil', alCambiar);
  }, []);

  // El alta de empresa y el login devuelven exactamente la misma respuesta
  // —el registro deja la sesion ya iniciada—, asi que se aplica igual.
  useEffect(() => {
    if (ajustesSesion) {
      localStorage.setItem(CLAVE_SESION, JSON.stringify(ajustesSesion));
    }
  }, [ajustesSesion]);

  function aplicarSesion(r) {
    // NO SE GUARDA NINGUNA CREDENCIAL · hallazgo A-4. El servidor ya
    // dejó la cookie `HttpOnly` en la respuesta de esta misma llamada;
    // aquí solo se apunta que hay sesión, para saber qué pintar.
    marcarSesion(true);
    if (r.sesion) setAjustesSesion(r.sesion);
    setMotivoCierre(null);
    setUsuario({
      id: r.id_usuario,
      nombre: r.nombre_completo,
      rol: r.rol,
      debe_cambiar_password: r.debe_cambiar_password,
      tipo_cuenta: r.tipo_cuenta,
      // SI EL CORREO ESTÁ CONFIRMADO · 30 de agosto de 2026.
      //
      // POR OMISIÓN, SÍ, y eso importa: una credencial guardada de antes
      // de este cambio no trae el dato, y tomarlo por «no confirmado»
      // mandaría a confirmar su correo a gente que lleva meses usando
      // TEGO. El guardián de verdad es el servidor, que contesta 403
      // `CORREO_SIN_VERIFICAR`; esto solo decide qué pantalla se pinta.
      correo_verificado: r.correo_verificado !== false,
      // ESTABILIDAD D1: a quien le falta el segundo factor se le abre el
      // asistente al entrar. Por omisión, NO: una sesión guardada de antes
      // no trae el dato y no se le interrumpe el trabajo por eso.
      debe_activar_2fa: r.debe_activar_2fa === true,
    });
  }

  // `extra` es opcional (código del segundo factor, recordar el aparato):
  // sin él, la llamada es la de siempre.
  // «ENTRA CON UN TOQUE LA PRÓXIMA VEZ» · DIRECTRIZ-LLAVE L-3 (R-LL-4).
  // Se ofrece después de entrar con contraseña (y código), no al recargar
  // ni al entrar con llave: vive en memoria y nace en las dos puertas de
  // contraseña. La tarjeta decide si de verdad se enseña (sin llaves,
  // navegador que las admite, y sin un «Ahora no» de esta persona).
  const [ofrecerLlave, setOfrecerLlave] = useState(false);

  async function iniciarSesion(email, password, extra) {
    aplicarSesion(await api.login(email, password, extra));
    setOfrecerLlave(true);
  }

  // ENTRAR CON LLAVE DE ACCESO · ESTABILIDAD D3. La huella o el Face ID
  // del aparato en lugar de la contraseña. Desde L-2 (DIRECTRIZ-LLAVE) el
  // correo es una pista opcional, y `opciones` lleva `mediation` y `signal`
  // para el relleno automático del campo de correo.
  async function iniciarSesionConLlave(pista, opciones) {
    aplicarSesion(await entrarConLlave(pista, opciones));
  }

  const [pospuesto, setPospuesto] = useState(leerPospuesto);
  function posponerSegundoFactor() {
    try { sessionStorage.setItem(CLAVE_POSPUESTO, '1'); } catch { /* sin almacén: solo por ahora */ }
    setPospuesto(true);
  }
  function marcarSegundoFactorActivo() {
    setUsuario((u) => (u ? { ...u, debe_activar_2fa: false } : u));
  }

  // LA TERCERA PUERTA · Accesos Fase 1. Para quien no tiene correo: su
  // empresa, su usuario y su contraseña. La clave de la empresa se
  // recuerda en este navegador —no es un secreto, va impresa en la tira
  // que le dieron— para que la segunda vez solo teclee usuario y clave.
  async function iniciarSesionConUsuario(claveEmpresa, nombreUsuario, password, extra) {
    const r = await api.loginUsuario(claveEmpresa, nombreUsuario, password, extra);
    recordarClaveEmpresa(claveEmpresa);
    aplicarSesion(r);
    setOfrecerLlave(true);
  }

  // Alta autoservicio. Termina DENTRO del sistema: el backend crea la
  // empresa, su suscripcion de prueba, la sucursal Matriz y el usuario
  // DUENO en una sola transaccion, y devuelve la sesion abierta. Mandar a
  // la pantalla de acceso despues de registrarse seria pedir la
  // contraseña que se acaba de escribir.
  async function registrarEmpresa(datos) {
    aplicarSesion(await api.post('/registro', datos));
  }

  // CERRAR DE VERDAD, no solo enseñar un error.
  //
  // `motivo` decide dos cosas: qué se le dice a la persona y si se
  // rescata lo que estaba escribiendo. A mano no se rescata —quien cierra
  // a propósito no espera encontrarse su borrador— y automáticamente sí.
  const cerrarSesion = useCallback((motivo = 'A_MANO') => {
    if (motivo !== 'A_MANO') guardarBorrador();
    else olvidarBorrador();

    // SE LE DICE AL SERVIDOR · deuda T-c.
    //
    // Hasta el 29 de agosto de 2026 esto solo borraba el token de aquí, y
    // una copia seguía abriendo TEGO hasta cumplir su hora. Ahora la
    // sesión se cierra de verdad y esa credencial deja de valer en su
    // siguiente petición.
    //
    // NO SE ESPERA LA RESPUESTA, y es a propósito: la sesión local se
    // cierra igual aunque el servidor no conteste. Quien pulsa «salir»
    // en un almacén sin red no puede quedarse dentro por eso —y el
    // token, si nadie lo cerró, muere solo a su hora—.
    //
    // NO SE MANDA SI LA SESIÓN YA VENCIÓ: sería una petición que el
    // servidor va a rechazar con 401, y ese 401 vuelve a llamar aquí.
    if (motivo === 'A_MANO' && haySesion()) {
      api.post('/auth/salir', {}).catch(() => {});
    }

    marcarSesion(false);
    setUsuario(null);
    setOfrecerLlave(false);
    olvidarPasoSeguro();
    setMotivoCierre(motivo);
    // En el teléfono no queda nada del turno. La caché ya va marcada con
    // el id del usuario y no se la devuelve a otro, pero un aparato de
    // almacén pasa de mano en mano y de empresa en empresa: esto es el
    // cinturón, aquello los tirantes.
    limpiarTrabajo();
  }, []);

  // EL AVISO DEL 401, EN UN SOLO SITIO · deuda T-a.
  //
  // `api.js` llama a esto cuando el servidor contesta 401 en cualquier
  // ruta que no sea la puerta. Antes cada pantalla enseñaba su mensaje y
  // nadie cerraba la sesión: el menú seguía completo y lo ya cargado
  // seguía a la vista.
  useEffect(() => {
    avisarmeSiSePierdeLaSesion(() => {
      // Solo si se creía dentro. Un 401 estando fuera no es noticia.
      if (haySesion()) cerrarSesion('CADUCADA');
    });
    return () => avisarmeSiSePierdeLaSesion(null);
  }, [cerrarSesion]);

  // Renovar la credencial mientras se trabaja. Silenciosa a propósito: si
  // falla, la siguiente petición dará 401 y de ahí sale el cierre — no
  // hacen falta dos caminos para lo mismo.
  const renovarSesion = useCallback(async () => {
    // CANDADO ENTRE PESTAÑAS · T-14. Si otra pestaña de este navegador
    // acaba de renovar, la cookie ya está fresca —la comparten— y esta se
    // ahorra la petición. Que dos pestañas no peleen por renovar.
    try {
      const ultima = Number(localStorage.getItem(CLAVE_RENOVADA) || 0);
      if (Date.now() - ultima < MARGEN_RENOVACION_MS) {
        marcarSesion(true);
        return;
      }
    } catch { /* modo privado: se renueva sin candado, no es peor que hoy */ }
    try {
      const r = await api.refrescar();
      // La cookie la renueva el servidor en esta misma respuesta.
      marcarSesion(true);
      if (r.sesion) setAjustesSesion(r.sesion);
      try {
        localStorage.setItem(CLAVE_RENOVADA, String(Date.now()));
      } catch { /* da igual: sin candado, se renueva de más y nada más */ }
    } catch { /* lo atiende el 401 */ }
  }, []);

  // Tras cambiar la contrasena, se limpia la bandera sin re-loguear.
  // Al confirmar el correo la pantalla lo dice aquí, y la consola se abre
  // sin recargar. Es la misma forma que `marcarPasswordCambiada`.
  function marcarCorreoConfirmado() {
    setUsuario((u) => (u ? { ...u, correo_verificado: true } : u));
  }

  function marcarPasswordCambiada() {
    setUsuario((u) => (u ? { ...u, debe_cambiar_password: false } : u));
  }

  const valor = {
    usuario,
    autenticado: !!usuario,
    motivoCierre,
    olvidarMotivoCierre: () => setMotivoCierre(null),
    ajustesSesion,
    renovarSesion,
    iniciarSesion,
    iniciarSesionConUsuario,
    iniciarSesionConLlave,
    marcarSegundoFactorActivo,
    registrarEmpresa,
    cerrarSesion,
    marcarPasswordCambiada,
    marcarCorreoConfirmado,
    tieneNivel: (min) => (usuario ? cumpleNivel(usuario.rol, min) : false),
    // La otra mitad del embudo, igual que en el backend: la CAPACIDAD.
    // `tieneNivel` contesta «¿alcanza su puesto?»; esto contesta «¿le
    // dieron esta llave?», que es lo que se puede conceder a una persona
    // concreta sin subirla de rango.
    puede: (permiso) => !!usuario?.permisos?.includes(permiso),
    // La gestión de subordinados es solo para cuentas de empresa. Ante un
    // token viejo sin el dato, se asume empresa (el backend es el guardián real).
    esEmpresa: !usuario || usuario.tipo_cuenta !== 'PERSONAL',
  };
  return <ContextoAuth.Provider value={valor}>{contenido()}</ContextoAuth.Provider>;

  // LO QUE SE PINTA ANTES QUE LA APLICACIÓN · ESTABILIDAD Parte D.
  //
  // Vive aquí y no en `App.jsx` (archivo caliente del integrador): el
  // carril engancha sus dos pantallas en su propio proveedor.
  //
  //   · «No fui yo»: el enlace del aviso se atiende con sesión o sin ella.
  //   · El asistente del segundo factor: después del cambio de contraseña
  //     obligado y de confirmar el correo —esas dos las decide `App.jsx`
  //     y van primero—, y solo si no se dejó para después en esta pestaña.
  function contenido() {
    const enlace = typeof window !== 'undefined' ? datosDelEnlace(window.location.search) : null;
    // Las tres pantallas de enlace de abajo también sustituyen a `App`: llevan
    // al lado el aviso de salida y la pregunta, para que con sesión atrás
    // tenga dónde pintarlos (TC-DR-AT, auditoría H2).
    if (enlace) return <><NoFuiYo token={enlace.token} empresa={enlace.empresa} /><NavegacionDePuerta /></>;
    // ESTABILIDAD Parte E (F6): «Recuperar mi cuenta» y el «No fui yo» de
    // una recuperación. Sin sesión, igual que el de arriba.
    const recuperar = typeof window !== 'undefined' ? enlaceDeRecuperacion(window.location.search) : null;
    if (recuperar) return <><RecuperarMiCuenta peticionInicial={recuperar.peticion} /><NavegacionDePuerta /></>;
    const detener = typeof window !== 'undefined' ? enlaceNoFuiYoRecuperacion(window.location.search) : null;
    if (detener) return <><RecuperacionNoFuiYo token={detener.token} empresa={detener.empresa} /><NavegacionDePuerta /></>;
    // ESTABILIDAD Parte C (F5): «Verifica tu empresa» va antes que el
    // segundo factor, y solo si el servidor dice que está encendida y le
    // toca. Apagada, `PuertaIdentidad` pinta lo de siempre.
    return (
      <PuertaIdentidad usuario={usuario} onSalir={() => cerrarSesion('A_MANO')}>
        {debeVerAsistente(usuario, pospuesto) ? (
          <>
            <AsistenteSegundoFactor
              onTerminar={marcarSegundoFactorActivo}
              onPosponer={posponerSegundoFactor}
            />
            <NavegacionDePuerta />
          </>
        ) : (
          <>
            {children}
            {ofrecerLlave && usuario && !usuario.debe_cambiar_password && usuario.correo_verificado !== false && (
              <TarjetaEntraConUnToque idUsuario={usuario.id} onCerrar={() => setOfrecerLlave(false)} />
            )}
          </>
        )}
      </PuertaIdentidad>
    );
  }
}
